/**
 * Deterministic Handoff Coach — plain-language guidance from existing handoff state.
 * No model calls. Only recommends actions the UI can actually perform.
 *
 * This coach reads the drafting state in front of the customer, which is the one
 * thing the server cannot see: unsaved request text, a draft mid-extraction, a
 * plan still running. What it cannot see is anything recorded against the contract
 * version that the browser was never told about — and when the server has decided
 * the requirements are not safe to hand off, a local "Ready to hand off" is not a
 * second opinion, it is a wrong answer the product will then refuse to honour. So
 * where the server has spoken about the requirements themselves, it wins.
 */

/**
 * Server blocking reasons that make the requirements unsafe to hand off at all,
 * and which no amount of local drafting state can clear. Deliberately narrow:
 * blockers belonging to later stages are already described by the coach's own
 * delivery and review states, and duplicating them would talk over guidance the
 * customer can act on.
 */
const REQUIREMENT_BLOCKING_CODES = ["open_contradiction"];

/**
 * The server blocker the coach must surrender to, or null. Accepts the snapshot
 * shape directly so callers do not each reimplement the selection.
 */
export function requirementBlockFromSnapshot(snapshot) {
  const reasons = Array.isArray(snapshot?.blockingReasons) ? snapshot.blockingReasons : [];
  const match = reasons.find((entry) => REQUIREMENT_BLOCKING_CODES.includes(entry?.code));
  if (!match) return null;
  return {
    code: match.code,
    detail: typeof match.detail === "string" ? match.detail : "",
    owner: match.owner || "product",
  };
}

const MACHINE_LABEL_RE =
  /\b(product_approved|engineering_acknowledged|insufficient_evidence|empty_criteria|open_unresolved_questions|INTENT_CONTRACT|schemaVersion|sourceClauseIds)\b/i;

/**
 * @typedef {'requester'|'engineer'|'reviewer'} CoachRole
 * @typedef {{ id: string, label: string }} CoachAction
 * @typedef {{
 *   id: string,
 *   role: CoachRole,
 *   headline: string,
 *   body: string,
 *   understood?: string|null,
 *   missing?: string[],
 *   primaryAction: CoachAction|null,
 *   secondaryActions?: CoachAction[],
 * }} HandoffCoachState
 */

function plural(n, singular, pluralWord = `${singular}s`) {
  return `${n} ${n === 1 ? singular : pluralWord}`;
}

function firstPromiseSummary(promiseTexts) {
  const list = Array.isArray(promiseTexts) ? promiseTexts.filter(Boolean) : [];
  if (!list.length) return null;
  const first = String(list[0]).trim();
  if (!first) return null;
  const short = first.length > 140 ? `${first.slice(0, 137)}…` : first;
  if (list.length === 1) {
    return `I’d include this promise in the handoff: “${short}”`;
  }
  return `I’d include ${list.length} promises. First: “${short}”`;
}

function requestSnippet(requestText, maxLen = 160) {
  const t = String(requestText || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!t) return null;
  return t.length > maxLen ? `${t.slice(0, maxLen - 1)}…` : t;
}

/**
 * Derive the Handoff Coach panel from current Feature Handoff data.
 * Priority: plan lifecycle → rejection → delivery/review → draft readiness → approve.
 *
 * @param {{
 *   planStatus?: string|null,
 *   planError?: object|null,
 *   isExtracting?: boolean,
 *   hasRequest?: boolean,
 *   requestText?: string|null,
 *   hasDraft?: boolean,
 *   promiseCount?: number,
 *   promiseTexts?: string[],
 *   openQuestionCount?: number,
 *   noteCount?: number,
 *   contractState?: string|null,
 *   approvalAllowed?: boolean|null,
 *   policyMode?: string|null,
 *   approvedWithWarnings?: boolean,
 *   hasEngAck?: boolean,
 *   hasEngineeringRejection?: boolean,
 *   rejectionReasonLabel?: string|null,
 *   hasDeliveryBranch?: boolean,
 *   hasAcceptanceReview?: boolean,
 *   hasAcceptanceCriteria?: boolean,
 *   canRetryPlanning?: boolean,
 * }} input
 * @returns {HandoffCoachState}
 */
export function deriveHandoffCoach(input = {}) {
  const planStatus = String(input.planStatus || "").toLowerCase();
  const preparing = Boolean(input.isExtracting);
  const hasRequest = Boolean(input.hasRequest);
  const requestText = String(input.requestText || "").trim();
  const hasDraft = Boolean(input.hasDraft);
  const promiseCount = Number(input.promiseCount) || 0;
  const openQuestionCount = Number(input.openQuestionCount) || 0;
  const noteCount = Number(input.noteCount) || 0;
  const contractState = input.contractState || null;
  const isApproved =
    contractState === "product_approved" ||
    contractState === "engineering_acknowledged";
  const hasEngAck = Boolean(input.hasEngAck);
  const hasEngineeringRejection = Boolean(input.hasEngineeringRejection);
  const hasDeliveryBranch = Boolean(input.hasDeliveryBranch);
  const hasAcceptanceReview = Boolean(input.hasAcceptanceReview);
  const hasAcceptanceCriteria = Boolean(input.hasAcceptanceCriteria);
  const policyMode = input.policyMode || "advisory";
  const approvalAllowed = input.approvalAllowed;
  const understood = firstPromiseSummary(input.promiseTexts);
  const askedFor = requestSnippet(requestText);
  const canRetryPlanning = Boolean(input.canRetryPlanning);

  // 1) Promise-draft extraction only. Engineering-plan generation must not block discovery.
  if (preparing) {
    return {
      id: "generating",
      role: "requester",
      headline: "Understanding your request",
      body: "I’m extracting promises and clarifications from your request and any reviewed screenshots. Engineering guidance can keep preparing in the background.",
      understood: askedFor
        ? `You asked: “${askedFor}”`
        : hasRequest
          ? "We have your request and are turning it into product decisions."
          : null,
      missing: [],
      primaryAction: null,
      secondaryActions: [],
    };
  }

  // 2) Failed / rejected planning
  if (planStatus === "failed") {
    return {
      id: "plan_failed",
      role: "requester",
      headline: "Planning didn’t finish",
      body:
        "Something went wrong while preparing the engineering plan. Retry planning, or make the request more specific and try again.",
      understood: hasRequest ? "We still have your request text." : null,
      missing: ["The engineering plan did not complete."],
      primaryAction: canRetryPlanning
        ? { id: "retry_planning", label: "Retry planning" }
        : { id: "make_request_specific", label: "Make request more specific" },
      secondaryActions: canRetryPlanning
        ? [{ id: "make_request_specific", label: "Make request more specific" }]
        : [],
    };
  }

  if (planStatus === "rejected") {
    return {
      id: "plan_rejected",
      role: "requester",
      headline: "This request doesn’t fit the repository well enough",
      body:
        "Revise the request to match what this codebase can support, then try planning again.",
      understood: hasRequest ? "We read your request, but repo fit looked weak." : null,
      missing: ["Planning rejected this request for repo fit."],
      primaryAction: { id: "make_request_specific", label: "Make request more specific" },
      secondaryActions: canRetryPlanning
        ? [{ id: "retry_planning", label: "Retry planning" }]
        : [],
    };
  }

  // 3) Insufficient evidence (before or without a usable draft)
  if (planStatus === "insufficient_evidence" && !hasDraft) {
    return {
      id: "insufficient_evidence",
      role: "requester",
      headline: "We need a clearer request before planning",
      body:
        "I understand the product idea, but I couldn’t confidently map it to the repo. Add details like where this appears, who uses it, and what result you expect.",
      understood: hasRequest
        ? "There is a product idea in your request, but evidence was too weak for a confident plan."
        : null,
      missing: [
        "Where in the product this shows up",
        "Who uses it",
        "What result you expect",
      ],
      primaryAction: {
        id: "make_request_specific",
        label: "Make request more specific",
      },
      secondaryActions: [
        { id: "create_promise_draft_anyway", label: "Create Task draft anyway" },
        ...(canRetryPlanning
          ? [{ id: "retry_planning", label: "Retry planning" }]
          : []),
      ],
    };
  }

  // 4) No request yet
  if (!hasRequest) {
    return {
      id: "needs_request",
      role: "requester",
      headline: "Start with your request",
      body:
        "Describe the change you want. I’ll turn it into promises the engineer can accept.",
      understood: null,
      missing: ["Request text is empty."],
      primaryAction: { id: "make_request_specific", label: "Write your request" },
      secondaryActions: [],
    };
  }

  // 5) The server says the requirements themselves are not safe to hand off.
  //
  // Placed after the local machinery states — a plan still running or a failed
  // extraction is about this screen, not about the agreement — and before every
  // state that would invite the customer onward. Nothing below here can be right
  // while this stands, because the API will refuse whatever it suggests.
  const requirementBlock = input.requirementBlock;
  if (requirementBlock && requirementBlock.code) {
    return {
      id: "requirements_blocked",
      role: "requester",
      headline: "These requirements conflict with each other",
      body: [
        requirementBlock.detail ||
          "Two of these requirements cannot both hold, so there is no version of this work that satisfies them.",
        "Settle which one holds before handing this off. Nothing built against the current version would be safe to accept.",
      ].join(" "),
      understood: understood,
      missing: [
        "Handoff, review and merge are all held until the conflict is settled.",
      ],
      primaryAction: { id: "resolve_requirement_conflict", label: "Review the conflict" },
      secondaryActions: [{ id: "revise_handoff", label: "Revise the requirements" }],
    };
  }

  // 6) Engineer rejected — requester must revise
  if (hasEngineeringRejection) {
    const reason = input.rejectionReasonLabel
      ? ` Reason: ${input.rejectionReasonLabel}.`
      : "";
    return {
      id: "engineer_rejected",
      role: "requester",
      headline: "Engineer requested changes",
      body: `They cannot accept this yet.${reason} Review their comment, revise promises/notes/questions, then approve again.`,
      understood: understood,
      missing: [
        "Engineer acceptance is locked until you revise and re-approve.",
        ...(openQuestionCount > 0
          ? [`${plural(openQuestionCount, "open question")} still on the draft`]
          : []),
      ],
      primaryAction: { id: "revise_handoff", label: "Revise handoff" },
      secondaryActions: [
        { id: "focus_questions", label: "Review questions" },
      ],
    };
  }

  // 7) Delivery / review (engineer accepted)
  if (hasEngAck) {
    if (hasAcceptanceReview) {
      return {
        id: "review_complete",
        role: "reviewer",
        headline: "Delivery review is on file",
        body:
          "A branch review already ran against the Task acceptance criteria. You can re-run it if the branch changed. Intent Contract review comes later.",
        understood: understood,
        missing: [],
        primaryAction: {
          id: "review_delivered_branch",
          label: "Review delivered branch",
        },
        secondaryActions: [],
      };
    }
    if (!hasDeliveryBranch) {
      return {
        id: "ready_for_delivery",
        role: "engineer",
        headline: "Ready for delivery",
        body:
          "The engineer accepted this handoff. Select the delivery branch when work is ready, then run the branch review.",
        understood: [
          understood,
          noteCount > 0 ? `${plural(noteCount, "note")} included for the engineer` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        missing: ["No delivery branch selected yet."],
        primaryAction: { id: "select_branch", label: "Select branch" },
        secondaryActions: [],
      };
    }
    if (!hasAcceptanceCriteria) {
      return {
        id: "review_no_criteria",
        role: "reviewer",
        headline: "Review is limited right now",
        body:
          "The engineer accepted the handoff and a branch is selected, but this Task has no acceptance criteria to review against. Improve the request/plan before treating review as meaningful.",
        understood: understood,
        missing: ["Task has no acceptance criteria."],
        primaryAction: { id: "make_request_specific", label: "Improve the request" },
        secondaryActions: [
          { id: "review_delivered_branch", label: "Open review anyway" },
        ],
      };
    }
    return {
      id: "review_ready",
      role: "reviewer",
      headline: "Review the delivered branch",
      body:
        "This review compares the branch to the Task acceptance criteria. Intent Contract review comes later.",
      understood: understood,
      missing: [],
      primaryAction: {
        id: "review_delivered_branch",
        label: "Review delivered branch",
      },
      secondaryActions: [],
    };
  }

  // 8) Approved — waiting for engineer
  if (isApproved && !hasEngAck) {
    const warnBits = [];
    if (input.approvedWithWarnings || openQuestionCount > 0) {
      warnBits.push(
        `${plural(openQuestionCount, "open question")} accepted as warnings under Advisory`
      );
    }
    if (noteCount > 0) {
      warnBits.push(`${plural(noteCount, "note")} for the engineer`);
    }
    return {
      id: "waiting_for_engineer",
      role: "engineer",
      headline: "Waiting for engineer",
      body: [
        `The handoff is approved with ${plural(promiseCount, "promise")}.`,
        "Accept if you can build this, or request changes if the agreement needs work.",
        warnBits.length ? warnBits.join(". ") + "." : "",
      ]
        .filter(Boolean)
        .join(" "),
      understood: [
        understood,
        noteCount > 0 ? `${plural(noteCount, "engineer note")} included` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      missing: warnBits,
      primaryAction: { id: "eng_accept", label: "Engineer accepts handoff" },
      secondaryActions: [
        { id: "eng_request_changes", label: "Request changes" },
      ],
    };
  }

  // 9) No draft yet
  if (!hasDraft) {
    if (planStatus === "insufficient_evidence") {
      return {
        id: "insufficient_evidence",
        role: "requester",
        headline: "We need a clearer request before planning",
        body:
          "I understand the product idea, but I couldn’t confidently map it to the repo. You can still create a Task draft to clarify the agreement.",
        understood: "Product intent exists; engineering map was not confident.",
        missing: [
          "Where this appears",
          "Who uses it",
          "What result you expect",
        ],
        primaryAction: {
          id: "make_request_specific",
          label: "Make request more specific",
        },
        secondaryActions: [
          { id: "create_promise_draft_anyway", label: "Create Task draft anyway" },
        ],
      };
    }
    return {
      id: "needs_draft",
      role: "requester",
      headline: "Turn your request into promises",
      body:
        "I’ll prepare a handoff draft from your request automatically. If that didn’t start, use Prepare handoff.",
      understood: askedFor ? `You asked: “${askedFor}”` : null,
      missing: [],
      primaryAction: { id: "prepare_handoff", label: "Prepare handoff" },
      secondaryActions: [
        { id: "make_request_specific", label: "Make request more specific" },
      ],
    };
  }

  // 10) Zero promises
  if (promiseCount === 0) {
    return {
      id: "zero_promises",
      role: "requester",
      headline: "No promises found yet",
      body:
        "A handoff needs at least one promise. Add one in your own words (prefer copying from the request), try again from the request, or make the request more specific.",
      understood: openQuestionCount
        ? `I found ${plural(openQuestionCount, "clarifying question")}, but no contractual promises yet.`
        : "I read the request, but could not form a contractual promise yet.",
      missing: ["At least one promise is required before approval."],
      primaryAction: { id: "add_promise", label: "Add a promise" },
      secondaryActions: [
        { id: "try_again_from_request", label: "Try again from request" },
        { id: "make_request_specific", label: "Make request more specific" },
      ],
    };
  }

  // 11) Strict blocked (questions)
  if (policyMode === "strict" && approvalAllowed === false && openQuestionCount > 0) {
    return {
      id: "strict_blocked",
      role: "requester",
      headline: "Strict handoff is blocked",
      body:
        "Answer the remaining material decisions before handing this Task to engineering. Advisory preview can still show warnings, but it cannot approve the handoff.",
      understood: understood,
      missing: [
        `${plural(openQuestionCount, "open question")} block Strict approval`,
      ],
      primaryAction: { id: "focus_questions", label: "Answer questions" },
      secondaryActions: [],
    };
  }

  // 12) Citation / hard not-ready (non-question)
  if (approvalAllowed === false && openQuestionCount === 0) {
    return {
      id: "not_ready",
      role: "requester",
      headline: "This draft isn’t ready to approve yet",
      body:
        "Something in this Task needs fixing before handoff (often a citation or missing detail). Review the warnings below, or refresh the draft from your request.",
      understood: understood,
      missing: ["Approval is blocked until the draft is fixed."],
      primaryAction: { id: "try_again_from_request", label: "Try again from request" },
      secondaryActions: [],
    };
  }

  // 13) Promises + open questions
  if (openQuestionCount > 0) {
    return {
      id: "promises_with_questions",
      role: "requester",
      headline: "I found the main promise, but a few details are unclear",
      body:
        "Answer the questions that would change implementation or acceptance. Remaining optional notes do not block handoff.",
      understood: understood,
      missing: [
        `${plural(openQuestionCount, "open question")} still unclear`,
        ...(noteCount > 0 ? [`${plural(noteCount, "note")} will be included for the engineer`] : []),
      ],
      primaryAction: { id: "focus_questions", label: "Answer questions" },
      secondaryActions: [],
    };
  }

  // 14) Ready to approve
  return {
    id: "ready_to_approve",
    role: "requester",
    headline: "Ready to hand off",
    body: `The engineer will be asked to accept ${plural(promiseCount, "promise")}${
      noteCount > 0 ? ` and ${plural(noteCount, "note")}` : ""
    }.`,
    understood: understood,
    missing: [],
    primaryAction: { id: "approve_with_warnings", label: "Approve handoff" },
    secondaryActions: [],
  };
}

/** True if text looks like a raw machine/API label (for tests / guardrails). */
export function coachTextLooksMachineLabeled(text) {
  return MACHINE_LABEL_RE.test(String(text || ""));
}

export const HANDOFF_COACH_ROLE_LABELS = {
  requester: "For requester",
  engineer: "For engineer",
  reviewer: "For reviewer",
};
